/**
 * MeasurementsTab — "מדידות" (nutrition module, Phase 2).
 *
 * The nutrition coach's body-measurement tracker for a single patient.
 * Three view modes: אחרונה (latest tiles) / היסטוריה (table) / גרפים (chart).
 *
 * Every column, tile, form field and chart series is driven by
 * MEASUREMENT_FIELDS from firebase.js — the same map the server validates
 * against — so adding a field there lights it up here with no edits.
 *
 * bmi / bmr / rmr are computed server-side on write; bmi / bmr may carry a
 * coach override (`<key>Manual`) entered through AddMeasurementModal.
 * A field the coach left empty is ABSENT from the document and renders "—",
 * never 0 (a zero would draw a real-looking point on the chart).
 *
 * Error codes: CK-10050 (load), CK-10051 (create), CK-10052 (update),
 *              CK-10053 (delete).
 */

import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ArrowDown,
  ArrowUp,
  ChartLine,
  ClockCounterClockwise,
  PencilSimple,
  Plus,
  Ruler,
  Trash,
  WarningCircle,
} from "@phosphor-icons/react";

import { MEASUREMENT_FIELDS } from "../../../firebase";
import {
  selectTherapistId,
  selectTherapistName,
} from "../../../store/slices/authSlice";
import {
  useCreateMeasurementMutation,
  useDeleteMeasurementMutation,
  useGetMeasurementsQuery,
  useUpdateMeasurementMutation,
} from "../../../store/services/measurementsApi";
import { formatHebrewDate } from "../../../utils/dateUtils";
import { useToast } from "../../../contexts/ToastContext";
import ConfirmDialog from "../../ui/ConfirmDialog";
import EmptyDraw from "../../ui/EmptyDraw";
import AddMeasurementModal from "./AddMeasurementModal";
import "./MeasurementsTab.css";
import ContentLoader from "../../shared/ContentLoader";

// ============================================================================
// Constants
// ============================================================================

const VIEW_TABS = [
  { id: "latest", label: "אחרונה", icon: Ruler },
  { id: "history", label: "היסטוריה", icon: ClockCounterClockwise },
  { id: "charts", label: "גרפים", icon: ChartLine },
];

/**
 * Metrics the server derives on write. Never editable, always displayed
 * alongside the measured fields.
 */
const DERIVED_METRICS = {
  bmi: { label: "BMI", hint: "מדד מסת גוף", unit: "", digits: 1 },
  bmr: { label: "BMR", hint: "חילוף חומרים בסיסי", unit: 'קק"ל', digits: 0 },
  rmr: { label: "RMR", hint: "חילוף חומרים במנוחה", unit: 'קק"ל', digits: 0 },
};

const FIELD_KEYS = Object.keys(MEASUREMENT_FIELDS);
const METRIC_KEYS = Object.keys(DERIVED_METRICS);
const ALL_KEYS = [...FIELD_KEYS, ...METRIC_KEYS];

/** Unified descriptor for a measured field or a derived metric. */
const describe = (key) =>
  MEASUREMENT_FIELDS[key]
    ? { ...MEASUREMENT_FIELDS[key], hint: "", digits: 1 }
    : DERIVED_METRICS[key];

const DELETE_REASON = "נמחקה מתוך לשונית המדידות";

// ============================================================================
// Helpers (presentation only)
// ============================================================================

const hasValue = (measurement, key) =>
  measurement &&
  measurement[key] !== undefined &&
  measurement[key] !== null &&
  measurement[key] !== "";

/** Round for display without a trailing ".0"; missing values become "—". */
const fmtValue = (value, digits = 1) => {
  if (value === undefined || value === null || value === "") return "—";
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  const factor = 10 ** digits;
  return String(Math.round(num * factor) / factor);
};

const chartAnimationsEnabled = () =>
  !(
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  );

// ============================================================================
// Tab
// ============================================================================

function MeasurementsTab({ patient, clinicId, patientId, onMissingBodyData }) {
  const therapistId = useSelector(selectTherapistId);
  const therapistName = useSelector(selectTherapistName);
  const { showSuccess, showError } = useToast();

  const [view, setView] = useState("latest");
  const [editorMeasurement, setEditorMeasurement] = useState(null); // doc = edit
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [measurementToDelete, setMeasurementToDelete] = useState(null);
  const [chartField, setChartField] = useState("weight");

  const {
    data: measurements = [],
    isLoading,
    error: loadError,
    refetch,
  } = useGetMeasurementsQuery(
    { clinicId, patientId },
    { skip: !clinicId || !patientId }
  );

  const [createMeasurement, { isLoading: isCreating }] =
    useCreateMeasurementMutation();
  const [updateMeasurement, { isLoading: isUpdating }] =
    useUpdateMeasurementMutation();
  const [deleteMeasurement, { isLoading: isDeleting }] =
    useDeleteMeasurementMutation();

  useEffect(() => {
    if (!loadError) return;
    console.error("[CK-10050] Error loading patient measurements:", {
      errorCode: "CK-10050",
      errorMessage: loadError?.message || "unknown",
      clinicId,
      patientId,
      timestamp: new Date().toISOString(),
    });
  }, [loadError, clinicId, patientId]);

  // ─── Derived data ───
  const latest = measurements[0] || null;
  const previous = measurements[1] || null;

  /** Keys that at least one measurement carries, in the canonical order. */
  const presentKeys = useMemo(
    () => ALL_KEYS.filter((key) => measurements.some((m) => hasValue(m, key))),
    [measurements]
  );

  const latestKeys = useMemo(
    () => (latest ? ALL_KEYS.filter((key) => hasValue(latest, key)) : []),
    [latest]
  );

  // Keep the chart on a field that actually has data.
  useEffect(() => {
    if (presentKeys.length === 0) return;
    if (presentKeys.includes(chartField)) return;
    setChartField(
      presentKeys.includes("weight") ? "weight" : presentKeys[0]
    );
  }, [presentKeys, chartField]);

  /** Oldest → newest, only rows carrying the charted field. */
  const chartRows = useMemo(() => {
    const spec = describe(chartField);
    if (!spec) return [];
    return measurements
      .filter((m) => hasValue(m, chartField))
      .map((m) => ({
        id: m.id,
        label: formatHebrewDate(m.date, "dd/MM/yy"),
        value: Number(m[chartField]),
      }))
      .reverse();
  }, [measurements, chartField]);

  const missingBodyFields = useMemo(() => {
    const missing = [];
    if (!patient?.gender) missing.push("מין");
    if (!patient?.birthDate) missing.push("תאריך לידה");
    return missing;
  }, [patient]);

  // ─── Handlers ───
  const openCreate = () => {
    setEditorMeasurement(null);
    setIsEditorOpen(true);
  };

  const openEdit = (measurement) => {
    setEditorMeasurement(measurement);
    setIsEditorOpen(true);
  };

  const closeEditor = () => {
    setIsEditorOpen(false);
    setEditorMeasurement(null);
  };

  const handleSubmit = async (data) => {
    if (editorMeasurement) {
      try {
        await updateMeasurement({
          clinicId,
          patientId,
          measurementId: editorMeasurement.id,
          data,
          therapistId,
          therapistName,
        }).unwrap();
        closeEditor();
        showSuccess("המדידה עודכנה בהצלחה");
      } catch (error) {
        console.error("[CK-10052] Error updating measurement:", {
          errorCode: "CK-10052",
          errorMessage: error?.message || error?.data?.message || "unknown",
          clinicId,
          patientId,
          measurementId: editorMeasurement.id,
          timestamp: new Date().toISOString(),
        });
        showError(
          error?.message || error?.data?.message || "עדכון המדידה נכשל, נסו שוב"
        );
      }
      return;
    }

    try {
      await createMeasurement({
        clinicId,
        patientId,
        data,
        therapistId,
        therapistName,
      }).unwrap();
      closeEditor();
      setView("latest");
      showSuccess("המדידה נשמרה בהצלחה");
    } catch (error) {
      console.error("[CK-10051] Error creating measurement:", {
        errorCode: "CK-10051",
        errorMessage: error?.message || error?.data?.message || "unknown",
        clinicId,
        patientId,
        timestamp: new Date().toISOString(),
      });
      showError(
        error?.message || error?.data?.message || "שמירת המדידה נכשלה, נסו שוב"
      );
    }
  };

  const handleConfirmDelete = async () => {
    if (!measurementToDelete) return;
    try {
      await deleteMeasurement({
        clinicId,
        patientId,
        measurementId: measurementToDelete.id,
        reason: DELETE_REASON,
        therapistId,
        therapistName,
      }).unwrap();
      setMeasurementToDelete(null);
      showSuccess("המדידה נמחקה");
    } catch (error) {
      console.error("[CK-10053] Error deleting measurement:", {
        errorCode: "CK-10053",
        errorMessage: error?.message || error?.data?.message || "unknown",
        clinicId,
        patientId,
        measurementId: measurementToDelete.id,
        timestamp: new Date().toISOString(),
      });
      showError(
        error?.message || error?.data?.message || "מחיקת המדידה נכשלה, נסו שוב"
      );
    }
  };

  // ─── Render pieces ───
  const renderDelta = (key) => {
    if (!previous || !hasValue(previous, key) || !hasValue(latest, key)) {
      return <span className="nmt-tile-delta muted">אין מדידה קודמת להשוואה</span>;
    }
    const spec = describe(key);
    const diff = Number(latest[key]) - Number(previous[key]);
    const rounded = Math.round(diff * 10) / 10;
    if (rounded === 0) {
      return <span className="nmt-tile-delta muted">ללא שינוי</span>;
    }
    const Arrow = rounded > 0 ? ArrowUp : ArrowDown;
    return (
      <span className="nmt-tile-delta">
        <Arrow size={11} weight="fill" />
        {fmtValue(Math.abs(rounded), spec.digits)} {spec.unit}
        <span className="nmt-tile-delta-note">מהמדידה הקודמת</span>
      </span>
    );
  };

  const renderLatest = () => {
    if (!latest) return renderEmpty();
    return (
      <div className="nmt-latest">
        <div className="nmt-latest-head">
          <span className="nmt-latest-date">
            נמדד בתאריך {formatHebrewDate(latest.date)}
          </span>
          <div className="nmt-latest-badges">
            {latest.source === "client" && (
              <span className="nmt-badge nmt-badge-client">נמדד ע״י הלקוח</span>
            )}
            <div className="nmt-actions">
              <button
                type="button"
                className="nmt-icon-btn"
                onClick={() => openEdit(latest)}
                aria-label="עריכת המדידה האחרונה"
                title="עריכה"
              >
                <PencilSimple size={14} weight="fill" />
              </button>
              <button
                type="button"
                className="nmt-icon-btn danger"
                onClick={() => setMeasurementToDelete(latest)}
                aria-label="מחיקת המדידה האחרונה"
                title="מחיקה"
              >
                <Trash size={14} weight="fill" />
              </button>
            </div>
          </div>
        </div>

        <div className="nmt-tiles">
          {latestKeys.map((key) => {
            const spec = describe(key);
            const isEstimated =
              key === "fatPercentage" && latest.fatPercentageEstimated === true;
            const isManual = latest[`${key}Manual`] === true;
            return (
              <div className="nmt-tile" key={key}>
                <div className="nmt-tile-top">
                  <span className="nmt-tile-label">{spec.label}</span>
                  {isEstimated && (
                    <span
                      className="nmt-badge nmt-badge-estimated"
                      title="חושב מהיקף המותניים, הצוואר והגובה"
                    >
                      מחושב
                    </span>
                  )}
                  {isManual && (
                    <span
                      className="nmt-badge nmt-badge-estimated"
                      title={
                        latest[`${key}Computed`] != null
                          ? `הוזן ידנית ע״י המאמן/ת. הערך המחושב: ${latest[`${key}Computed`]}`
                          : "הוזן ידנית ע״י המאמן/ת"
                      }
                    >
                      הוזן ידנית
                    </span>
                  )}
                </div>
                <span className="nmt-tile-value">
                  {fmtValue(latest[key], spec.digits)}
                  {spec.unit && (
                    <span className="nmt-tile-unit">{spec.unit}</span>
                  )}
                </span>
                {spec.hint && <span className="nmt-tile-hint">{spec.hint}</span>}
                {renderDelta(key)}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderHistory = () => {
    if (measurements.length === 0) return renderEmpty();
    return (
      <div className="nmt-table-wrap">
        <table className="nmt-table">
          <caption className="nmt-table-caption">
            כל המדידות, מהחדשה לישנה
          </caption>
          <thead>
            <tr>
              <th scope="col">תאריך</th>
              {presentKeys.map((key) => {
                const spec = describe(key);
                return (
                  <th scope="col" key={key}>
                    {spec.label}
                    {spec.unit && (
                      <span className="nmt-th-unit"> ({spec.unit})</span>
                    )}
                  </th>
                );
              })}
              <th scope="col" className="nmt-th-actions">
                <span className="nmt-sr-only">פעולות</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {measurements.map((measurement) => (
              <tr className="nmt-row" key={measurement.id}>
                <td data-label="תאריך" className="nmt-cell-date">
                  <span className="nmt-date">
                    {formatHebrewDate(measurement.date)}
                  </span>
                  {measurement.source === "client" && (
                    <span className="nmt-badge nmt-badge-client">
                      נמדד ע״י הלקוח
                    </span>
                  )}
                </td>
                {presentKeys.map((key) => {
                  const spec = describe(key);
                  const isEstimated =
                    key === "fatPercentage" &&
                    measurement.fatPercentageEstimated === true;
                  const isManual = measurement[`${key}Manual`] === true;
                  return (
                    <td data-label={spec.label} key={key}>
                      <span className="nmt-value">
                        {fmtValue(measurement[key], spec.digits)}
                      </span>
                      {isEstimated && (
                        <span className="nmt-badge nmt-badge-estimated">
                          מחושב
                        </span>
                      )}
                      {isManual && (
                        <span className="nmt-badge nmt-badge-estimated" title="הוזן ידנית">
                          ידני
                        </span>
                      )}
                    </td>
                  );
                })}
                <td className="nmt-cell-actions">
                  <div className="nmt-actions">
                    <button
                      type="button"
                      className="nmt-icon-btn"
                      onClick={() => openEdit(measurement)}
                      aria-label="עריכת מדידה"
                      title="עריכה"
                    >
                      <PencilSimple size={14} weight="fill" />
                    </button>
                    <button
                      type="button"
                      className="nmt-icon-btn danger"
                      onClick={() => setMeasurementToDelete(measurement)}
                      aria-label="מחיקת מדידה"
                      title="מחיקה"
                    >
                      <Trash size={14} weight="fill" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderCharts = () => {
    if (measurements.length === 0) return renderEmpty();
    const spec = describe(chartField);
    return (
      <div className="nmt-charts">
        <div className="nmt-chips" role="group" aria-label="בחירת מדד לגרף">
          {presentKeys.map((key) => {
            const chipSpec = describe(key);
            const isActive = key === chartField;
            return (
              <button
                key={key}
                type="button"
                className={`nmt-chip ${isActive ? "active" : ""}`}
                aria-pressed={isActive}
                onClick={() => setChartField(key)}
              >
                {chipSpec.label}
              </button>
            );
          })}
        </div>

        {chartRows.length < 2 ? (
          <div className="nmt-chart-hint">
            <ChartLine size={24} weight="fill" />
            <p>
              נדרשות לפחות שתי מדידות של {spec?.label || "המדד"} כדי להציג גרף
              מגמה. הוסיפו מדידה נוספת כדי לראות את ההתקדמות.
            </p>
          </div>
        ) : (
          <div className="nmt-chart" dir="ltr">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart
                data={chartRows}
                margin={{ top: 16, right: 8, left: 8, bottom: 0 }}
              >
                <CartesianGrid
                  vertical={false}
                  stroke="var(--color-border-light)"
                />
                {/* reversed: time reads right-to-left, like the rest of the UI */}
                <XAxis
                  dataKey="label"
                  reversed
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 11, fill: "var(--color-text-secondary)" }}
                />
                <YAxis
                  orientation="right"
                  width={44}
                  tickLine={false}
                  axisLine={false}
                  domain={["auto", "auto"]}
                  tick={{ fontSize: 11, fill: "var(--color-text-secondary)" }}
                />
                <RechartsTooltip
                  cursor={{ stroke: "var(--color-border)" }}
                  formatter={(value) => [
                    `${fmtValue(value, spec.digits)} ${spec.unit}`.trim(),
                    spec.label,
                  ]}
                  contentStyle={{
                    direction: "rtl",
                    fontSize: "0.75rem",
                    borderRadius: "var(--radius-md)",
                    border: "1px solid var(--color-border-light)",
                    boxShadow: "var(--shadow-md)",
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  name={spec.label}
                  stroke="var(--color-primary)"
                  strokeWidth={2}
                  dot={{ r: 3, fill: "var(--color-primary-dark)" }}
                  activeDot={{ r: 5 }}
                  connectNulls={false}
                  isAnimationActive={chartAnimationsEnabled()}
                  animationDuration={500}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    );
  };

  const renderEmpty = () => (
    <div className="nmt-empty">
      <EmptyDraw variant="treatment" />
      <p>עדיין לא נרשמו מדידות עבור מטופל זה</p>
      <button type="button" className="nmt-primary-btn" onClick={openCreate}>
        <Plus size={15} weight="bold" />
        <span>הוספת מדידה ראשונה</span>
      </button>
    </div>
  );

  return (
    <div className="nmt-tab">
      <div className="nmt-header">
        <div className="nmt-header-title">
          <span className="nmt-header-icon">
            <Ruler size={20} weight="fill" />
          </span>
          <div className="nmt-header-text">
            <span className="nmt-eyebrow">מעקב תזונתי</span>
            <h3>מדידות</h3>
          </div>
        </div>
        <button type="button" className="nmt-primary-btn" onClick={openCreate}>
          <Plus size={15} weight="bold" />
          <span>הוספת מדידה</span>
        </button>
      </div>

      {missingBodyFields.length > 0 && (
        <div className="nmt-notice" role="status">
          <WarningCircle size={20} weight="fill" />
          <div className="nmt-notice-text">
            <strong>חסרים פרטי גוף לחישוב</strong>
            <p>
              ללא {missingBodyFields.join(" ו")} לא ניתן לחשב BMR ו-RMR עבור
              מדידות חדשות. שאר המדידות נשמרות כרגיל.
            </p>
          </div>
          {onMissingBodyData && (
            <button
              type="button"
              className="nmt-secondary-btn"
              onClick={onMissingBodyData}
            >
              השלמת פרטי המטופל
            </button>
          )}
        </div>
      )}

      <div className="nmt-card">
        <div className="nmt-tabs" role="tablist" aria-label="תצוגת המדידות">
          {VIEW_TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = view === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`nmt-tab-btn ${isActive ? "active" : ""}`}
                onClick={() => setView(tab.id)}
              >
                <Icon size={15} weight={isActive ? "fill" : "regular"} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        <div className="nmt-body">
          {isLoading ? (
            <ContentLoader fill message="טוען מדידות..." />
          ) : loadError ? (
            <div className="nmt-empty">
              <EmptyDraw variant="search" />
              <p>טעינת המדידות נכשלה</p>
              <button
                type="button"
                className="nmt-secondary-btn"
                onClick={() => refetch()}
              >
                נסו שוב
              </button>
            </div>
          ) : view === "latest" ? (
            renderLatest()
          ) : view === "history" ? (
            renderHistory()
          ) : (
            renderCharts()
          )}
        </div>
      </div>

      {isEditorOpen && (
        <AddMeasurementModal
          measurement={editorMeasurement}
          isSaving={isCreating || isUpdating}
          onClose={closeEditor}
          onSubmit={handleSubmit}
        />
      )}

      <ConfirmDialog
        isOpen={!!measurementToDelete}
        onClose={() => setMeasurementToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="מחיקת מדידה"
        message="המדידה תוסר מהמעקב ומהגרפים ולא ניתן יהיה לשחזר אותה. להמשיך?"
        confirmText="מחיקה"
        cancelText="ביטול"
        type="danger"
        icon="delete"
        confirmDisabled={isDeleting}
      />
    </div>
  );
}

export default MeasurementsTab;
